import { RealEstateObject } from '@immosky/types';
import { GridAlgorithm, MarkerClusterer } from '@googlemaps/markerclusterer';
import { MapMarkerRenderer } from './map-marker-renderer';
import { MapMarkerFactory } from './map-marker-factory';
import { BehaviorSubject, Observable } from 'rxjs';
import { MapIconFactory } from './map-icon-factory';

export interface MapClusterRendererConfig {
  map: google.maps.Map;
  gridSize: number;
  iconFactory: MapIconFactory,
  markerFactory: MapMarkerFactory
}

export class MapClusterRenderer {
  private readonly map: google.maps.Map;
  private readonly iconFactory: MapIconFactory;
  private readonly markerFactory: MapMarkerFactory;

  private objects: RealEstateObject[] = [];
  private markers: google.maps.Marker[] = [];
  private clusterer!: MarkerClusterer;

  private selectedMarker: google.maps.Marker | null = null;
  private selectedObjects$ = new BehaviorSubject<RealEstateObject[]>([]);

  get selectedObjects(): Observable<RealEstateObject[]> {
    return this.selectedObjects$.asObservable();
  }

  constructor(
    private readonly config: MapClusterRendererConfig,
  ) {
    this.map = this.config.map;
    this.iconFactory = this.config.iconFactory;
    this.markerFactory = this.config.markerFactory;
    this.clusterer = this.createClusterer();
  }

  private createClusterer(): MarkerClusterer {
    return new MarkerClusterer({
      map: this.map,
      algorithm: new GridAlgorithm({
        viewportPadding: 32,
        gridSize: this.config.gridSize,
      }),
      renderer: new MapMarkerRenderer(this.markerFactory),
      onClusterClick: (event, cluster) => {
        this.select(cluster.marker, cluster.count);

        if (cluster.markers) {
          const objects =
            cluster.markers
              .filter((marker) => !!marker.get('object'))
              .map((marker) => {
                return marker.get('object') as RealEstateObject;
              }) || [];

          this.selectedObjects$.next(objects);
        }
      },
    });
  }

  update(objects: RealEstateObject[]) {
    this.objects = objects;
    this.clear();
    this.createMarkers();
  }

  clear() {
    for (const marker of this.markers) {
      marker.setMap(null);
    }
    this.markers.length = 0;
    this.clusterer.clearMarkers();
    this.clusterer.setMap(null);
  }

  private createMarkers() {
    // have to create new cluster everytime, because somehow markers weren't cleared correctly
    this.clusterer = this.createClusterer();
    this.markers = [];

    for (const object of this.objects) {
      const marker = this.markerFactory.create({
        position: object.location.coordinates || { lat: 0, lng: 0 },
        count: 1,
      });

      marker.set('object', object);
      marker.setClickable(true);
      marker.addListener('click', () => {
        this.select(marker, 1);
        this.selectedObjects$.next([object]);
      });

      this.markers.push(marker);
    }

    this.clusterer.addMarkers(this.markers);
  }

  private select(marker: google.maps.Marker, count: number) {
    this.clearSelected();

    marker.set('count', count);
    marker.setIcon(this.iconFactory.createSelectedIconForCount(count));

    this.selectedMarker = marker;
  }

  clearSelected() {
    if (this.selectedMarker) {
      const count: number = this.selectedMarker.get('count');
      this.selectedMarker.setIcon(this.iconFactory.createIconForCount(count));
      this.selectedMarker = null;
    }
  }
}
